"use client";

import type { ReactNode } from "react";
import {
	CONTENT_TOOLTIP_SHORT,
	ContentTooltip,
} from "../design-system/tooltip";
import * as React from "react";
import { cn } from "../../lib/cn";
export interface IntentProgressSegment {
	value: number;
	className?: string;
	tooltip?: string;
}

export interface IntentProgressLayer {
	/** Stable key so fills stay mounted while values animate. */
	id?: string;
	value: number;
	className?: string;
	style?: React.CSSProperties;
	tooltip?: string;
}

export interface IntentProgressBarProps
	extends React.HTMLAttributes<HTMLDivElement> {
	value?: number | null;
	isIndeterminate?: boolean;
	trackClassName?: string;
	indicatorClassName?: string;
	/** Inline styles on the fill layer (e.g. CSS vars for banded metered colours). */
	indicatorStyle?: React.CSSProperties;
	segments?: IntentProgressSegment[];
	/** Full-length stacked fills rendered in array order (later layers on top). */
	layers?: IntentProgressLayer[];
	/**
	 * Optional layer behind the fill (e.g. decorative glow or projected ghost).
	 * The current-value fill is clipped independently so `translateX` / `scaleX`
	 * indicators do not leak; the overlay itself may extend outside the rail.
	 */
	trackOverlay?: ReactNode;
	/**
	 * When true (default), the fill uses a short CSS transform transition.
	 * Set false when `value` updates every animation frame (e.g. countdown timers);
	 * otherwise the transition fights rapid updates and looks jumpy.
	 */
	animateIndicator?: boolean;
}

export function IntentProgressBar({
	value = 0,
	isIndeterminate = false,
	className,
	trackClassName,
	indicatorClassName,
	indicatorStyle,
	segments,
	layers,
	trackOverlay,
	animateIndicator = true,
	...props
}: IntentProgressBarProps) {
	const normalizedSegments = (segments ?? []).map((segment) => ({
		...segment,
		value: Math.max(0, Math.min(100, Number(segment.value ?? 0))),
	}));
	const normalizedLayers = (layers ?? []).map((layer) => ({
		...layer,
		value: Math.max(0, Math.min(100, Number(layer.value ?? 0))),
	}));
	const clampedValue =
		normalizedSegments.length > 0
			? Math.max(
					0,
					Math.min(
						100,
						normalizedSegments.reduce(
							(sum, segment) => sum + segment.value,
							0,
						),
					),
				)
			: normalizedLayers.length > 0
				? Math.max(
						0,
						Math.min(
							100,
							normalizedLayers.reduce(
								(max, layer) => Math.max(max, layer.value),
								0,
							),
						),
					)
				: Math.max(0, Math.min(100, Number(value ?? 0)));
	const hasTrackOverlay = trackOverlay != null;

	const fillContent =
		normalizedSegments.length > 0 ? (
			<>
				{(() => {
					let leftOffset = 0;
					return normalizedSegments
						.filter((segment) => segment.value > 0)
						.map((segment, index) => {
							const left = leftOffset;
							leftOffset += segment.value;
							const segmentNode = (
								<div
									className={cn(
										"absolute inset-y-0 motion-safe:transition-[left,width] motion-safe:duration-300 motion-safe:ease-out",
										segment.className,
										segment.tooltip
											? "cursor-help"
											: undefined,
									)}
									style={{
										left: `${left}%`,
										width: `${segment.value}%`,
									}}
								/>
							);
							if (!segment.tooltip) {
								return (
									<div key={`segment-${left}-${index}`}>
										{segmentNode}
									</div>
								);
							}
							return (
								<ContentTooltip
									key={`segment-${left}-${index}`}
									asChild
									{...CONTENT_TOOLTIP_SHORT}
									side="top"
									content={segment.tooltip}
								>
									{segmentNode}
								</ContentTooltip>
							);
						});
				})()}
			</>
		) : normalizedLayers.length > 0 ? (
			<>
				{normalizedLayers.map((layer, index) => {
					const layerKey = layer.id ?? String(index);
					const layerNode = (
						<div
							className={cn(
								"absolute inset-y-0 left-0 w-full origin-left rounded-full ease-out motion-safe:transition-transform",
								animateIndicator && "motion-safe:duration-300",
								layer.className,
							)}
							style={{
								...layer.style,
								transform: `scaleX(${layer.value / 100})`,
								zIndex: index + 1,
							}}
						/>
					);
					if (!layer.tooltip) {
						return <div key={layerKey}>{layerNode}</div>;
					}
					return (
						<ContentTooltip
							key={layerKey}
							asChild
							{...CONTENT_TOOLTIP_SHORT}
							side="top"
							content={layer.tooltip}
						>
							{layerNode}
						</ContentTooltip>
					);
				})}
			</>
		) : (
			<div
				className={cn(
					"size-full rounded-full bg-primary",
					animateIndicator &&
						"motion-safe:transition-transform motion-safe:duration-150 motion-safe:ease-out",
					isIndeterminate && "w-1/2 animate-pulse",
					indicatorClassName,
				)}
				style={
					isIndeterminate
						? indicatorStyle
						: {
								...indicatorStyle,
								transform: `translateX(-${100 - clampedValue}%)`,
							}
				}
			/>
		);

	return (
		<div
			className={cn("w-full", className)}
			role="progressbar"
			aria-valuemin={0}
			aria-valuemax={100}
			aria-valuenow={isIndeterminate ? undefined : clampedValue}
			aria-label={props["aria-label"] ?? "Progress"}
			{...props}
		>
			<div
				className={cn(
					"relative isolate h-4 w-full rounded-full bg-border",
					hasTrackOverlay ? "overflow-visible" : "overflow-hidden",
					trackClassName,
				)}
			>
				{trackOverlay}
				{hasTrackOverlay ? (
					<div className="pointer-events-none absolute inset-0 z-[2] overflow-hidden rounded-full">
						{fillContent}
					</div>
				) : (
					fillContent
				)}
			</div>
		</div>
	);
}
